import { AddDocumentComponent } from '@/app/atoms/add-document/add-document.component';
import { WysiswygComponent } from '@/app/atoms/wysiswyg/wysiswyg.component';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { ComplianceDocsService } from '@/app/services/compliance-docs/compliance-docs.service';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { CreateMonitoring, MonitoringProvisionMode } from '../../services/create-monitoring';

interface ComplianceDoc {
  id: string;
  complianceName: string;
  description?: string;
}

@Component({
  selector: 'cmb-monitoring-requirements',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AddDocumentComponent, WysiswygComponent],
  templateUrl: './monitoring-requirements.html',
  styleUrl: './monitoring-requirements.scss',
})
export class MonitoringRequirements implements OnInit, OnDestroy {

  readonly provisionOptions: { id: MonitoringProvisionMode; name: string; description: string }[] = [
    { id: 'upload', name: 'Upload document', description: 'Upload a PDF, Word, or PowerPoint that defines the brief.' },
    { id: 'type', name: 'Type requirements', description: 'Write what content must show, and what is not allowed.' },
  ];

  readonly complianceDocs = signal<ComplianceDoc[]>([]);

  public readonly createMonitoringService = inject(CreateMonitoring);
  private readonly complianceService = inject(ComplianceDocsService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly subscriptionList = new Subscription();

  ngOnInit(): void {
    const brandId = this.currentUserService.focussedBrand?.id;
    if (!brandId) {
      return;
    }
    this.subscriptionList.add(
      this.complianceService.getBrandComplianceDocs(brandId, true).subscribe({
        next: (response) => this.complianceDocs.set(this.readDocs(response)),
        error: () => this.complianceDocs.set([]),
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  selectProvision(mode: MonitoringProvisionMode): void {
    this.createMonitoringService.provisionMode.set(mode);
  }

  onDocumentUploaded(event: { file: string; fileKey: string; fullUrl: string }): void {
    this.createMonitoringService.requirementsDocumentUrl.set(event.fullUrl);
  }

  onRequirements(html: string): void {
    this.createMonitoringService.requirementsHtml.set(html);
  }

  onDonts(html: string): void {
    this.createMonitoringService.restrictionsHtml.set(html);
  }

  toggleCompliance(id: string): void {
    const current = this.createMonitoringService.selectedComplianceIds();
    if (current.includes(id)) {
      this.createMonitoringService.selectedComplianceIds.set(current.filter((item) => item !== id));
      return;
    }
    this.createMonitoringService.selectedComplianceIds.set([...current, id]);
  }

  isComplianceSelected(id: string): boolean {
    return this.createMonitoringService.selectedComplianceIds().includes(id);
  }

  private readDocs(response: unknown): ComplianceDoc[] {
    if (!Array.isArray(response)) {
      return [];
    }
    return response.flatMap((item) => {
      if (typeof item !== 'object' || item === null || !('id' in item)) {
        return [];
      }
      const record = item as { id: unknown; complianceName?: unknown; description?: unknown };
      if (typeof record.id !== 'string') {
        return [];
      }
      return [{
        id: record.id,
        complianceName: typeof record.complianceName === 'string' ? record.complianceName : 'Compliance document',
        description: typeof record.description === 'string' ? record.description : undefined,
      }];
    });
  }
}
